import { useEffect, useRef, useCallback } from "react";
import { toast } from "react-hot-toast";
import {
  EVENTS,
  KEYBOARD_KEYS,
  IGNORE_SCANNER_KEYS,
  STORAGE_KEYS,
  DEFAULT_BATCH_BARCODE_CONFIG,
  HTML_TAGS,
  MODAL_SELECTORS,
} from "const";
import { eventsEmitter } from "utils/events";
import * as TerminalHelper from "./TermialHelper";

const CODE_SCANNER_DELAY = 100;
const SCAN_STREAM_TIMEOUT = 350;
let isScanningBarcodeFlag = false;
let lastScanCompletedTime = 0;

/**
 * Returns true if a rapid barcode scan stream is currently active or recently completed.
 */
export function isScanningBarcode() {
  return isScanningBarcodeFlag || Date.now() - lastScanCompletedTime < 400;
}

/**
 * Checks if the currently focused element is a restricted input (textarea, modal popup, contenteditable)
 * where keystrokes should NEVER be intercepted as barcode scan data.
 */
export function isRestrictedInputActive() {
  const activeEl = document.activeElement;
  if (!activeEl) return false;
  return (
    activeEl.tagName === HTML_TAGS.TEXTAREA ||
    activeEl.isContentEditable ||
    Boolean(activeEl.closest?.(MODAL_SELECTORS))
  );
}

export function findMatchingProducts(availableItems, searchKey) {
  if (!searchKey) return [];
  const normalizedKey = String(searchKey).trim().toLowerCase();
  const numericKey = !isNaN(Number(searchKey)) ? String(Number(searchKey)) : null;

  return (availableItems || []).filter((item) => {
    const ean = item.ean !== undefined && item.ean !== null ? String(item.ean).trim().toLowerCase() : null;
    const plu = item.plu !== undefined && item.plu !== null ? String(item.plu).trim().toLowerCase() : null;
    const pluNumeric = plu && !isNaN(Number(plu)) ? String(Number(plu)) : null;

    if (ean && (ean === normalizedKey || (numericKey && ean === numericKey))) return true;
    if (plu && (plu === normalizedKey || (numericKey && pluNumeric === numericKey))) return true;

    return false;
  });
}

export function handleMatches(availableItems, searchKey, quantity, price, uniqueItemsToAdd, selectableMatch) {
  let matches = findMatchingProducts(availableItems, searchKey);

  if (price !== null) {
    matches = matches.map((m) => ({ ...m, sale_price: price, price: price }));
  }

  if (matches.length === 1) {
    uniqueItemsToAdd.push({
      item: matches[0],
      qty: quantity,
      price: price,
      rate: price,
    });
  } else if (matches.length > 1 && !selectableMatch.value) {
    selectableMatch.value = { matches, qty: quantity, price: price };
  }
}

export function tryParseOnlineOrderScan(trimmedCode, setOnlineOrderId, setOnlineOrderSecret, setOnlineOrderOpen) {
  const onlineOrderPrefixes = ["oo:", "https://qr.sanoft.com/oo?d="];
  const matchPrefix = onlineOrderPrefixes.find(
    (p) => trimmedCode.startsWith(p) || trimmedCode.toLowerCase().startsWith(p),
  );
  if (!matchPrefix) return false;

  const jsonStr = trimmedCode.substring(matchPrefix.length).trim();
  try {
    const orderInfo = JSON.parse(jsonStr);
    if (orderInfo && orderInfo.id) {
      setOnlineOrderId(orderInfo.id);
      setOnlineOrderSecret(orderInfo.secret || null);
      setOnlineOrderOpen(true);
      return true;
    }
  } catch (e) {
    console.error("Failed to parse online order QR code JSON:", e);
    toast.error("Invalid Online Order QR Code");
  }
  return false;
}

function getBatchBarcodeConfig() {
  let pluLen = DEFAULT_BATCH_BARCODE_CONFIG.PLU_LEN;
  let weightLen = DEFAULT_BATCH_BARCODE_CONFIG.WEIGHT_LEN;
  let priceLen = DEFAULT_BATCH_BARCODE_CONFIG.PRICE_LEN;

  try {
    const stored = localStorage.getItem(STORAGE_KEYS.BATCH_BARCODE_CONFIG);
    if (stored) {
      const config = JSON.parse(stored);
      pluLen = Number(config.pluLength) || 4;
      weightLen = Number(config.weightLength) || 5;
      priceLen = Number(config.priceLength) || 6;
    }
  } catch (e) {
    console.error("Error reading batchBarcodeConfig", e);
  }

  const settings = TerminalHelper.getTerminalSettings();
  const isThreeFraction = settings?.amountWithThreeFraction;
  const priceDecimals = isThreeFraction ? 3 : 2;
  const priceDivisor = isThreeFraction ? 1000 : 100;

  return { pluLen, weightLen, priceLen, priceDecimals, priceDivisor };
}

function parseSingleBatchItem(itemCode, config, availableItems, uniqueItemsToAdd, selectableMatch) {
  const { pluLen, weightLen, priceLen, priceDecimals, priceDivisor } = config;
  const expectedLengthWithWeight = pluLen + weightLen;
  const expectedLengthWithWeightAndPrice = expectedLengthWithWeight + priceLen;

  if (itemCode.length < expectedLengthWithWeight) return;

  const pluPart = itemCode.substring(0, pluLen);
  const weightPart = itemCode.substring(pluLen, expectedLengthWithWeight);

  const qtyFraction = Number(weightPart.substring(weightPart.length - 3)) / 1000;
  const qtyInt = Number(weightPart.substring(0, weightPart.length - 3));
  const quantity = qtyInt + qtyFraction;

  let price = null;
  if (itemCode.length >= expectedLengthWithWeightAndPrice) {
    const pricePart = itemCode.substring(expectedLengthWithWeight, expectedLengthWithWeightAndPrice);
    const rateFraction = Number(pricePart.substring(pricePart.length - priceDecimals)) / priceDivisor;
    const rateInt = Number(pricePart.substring(0, pricePart.length - priceDecimals));
    price = rateInt + rateFraction;
  }

  const plu = String(Number(pluPart));
  handleMatches(availableItems, plu, quantity, price, uniqueItemsToAdd, selectableMatch);
}

export function parseBatchBarcodeItems(scannedCode, availableItems, uniqueItemsToAdd, selectableMatch) {
  const config = getBatchBarcodeConfig();
  const lines = (scannedCode || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const rawItems = [];

  lines.forEach((line) => {
    const tokens = line
      .split("$")
      .map((t) => t.trim())
      .filter(Boolean);
    tokens.forEach((t) => rawItems.push(t));
  });

  rawItems.forEach((item) => {
    const itemCode = item.replace(/\s+/g, "");
    parseSingleBatchItem(itemCode, config, availableItems, uniqueItemsToAdd, selectableMatch);
  });
}

export function processScannedItem(code, availableItems, uniqueItemsToAdd, selectableMatch) {
  const itemCode = code.trim();

  if (itemCode.startsWith("$")) return;

  if (itemCode.toLowerCase().startsWith("plu")) {
    const plu = itemCode.replace(/plu\s*-?\s*/i, "").trim();
    handleMatches(availableItems, plu, 1, null, uniqueItemsToAdd, selectableMatch);
    return;
  }

  if (itemCode.length > 0) {
    handleMatches(availableItems, itemCode, 1, null, uniqueItemsToAdd, selectableMatch);
  }
}

export function processScannedBarcodeItems(
  scannedCode,
  availableItems,
  uniqueItemsToAdd,
  selectableMatch,
  handleBatchAddItemsToSale,
) {
  const trimmedCode = (scannedCode || "").trim();
  if (!trimmedCode) return;

  if (trimmedCode.startsWith("$") || trimmedCode.includes("$")) {
    parseBatchBarcodeItems(trimmedCode, availableItems, uniqueItemsToAdd, selectableMatch);
    if (uniqueItemsToAdd.length > 0) {
      return handleBatchAddItemsToSale(uniqueItemsToAdd);
    }
    return;
  }

  const scannedItems = trimmedCode.split(/\r?\n/).filter(Boolean);

  scannedItems.forEach((code) => {
    processScannedItem(code, availableItems, uniqueItemsToAdd, selectableMatch);
  });

  if (uniqueItemsToAdd.length > 0) {
    return handleBatchAddItemsToSale(uniqueItemsToAdd);
  }
}

/**
 * Restores an input or textarea element's value to its pre-scan state,
 * preventing any leaked initial scanner character from corrupting the UI field.
 * Does NOT blur the element, keeping focus and cursor position stable.
 */
export function restoreInputElementValue(el, preVal, preSel, leakedChar) {
  if (!el) return;

  const isInput = el.tagName === HTML_TAGS.INPUT;
  const isTextArea = el.tagName === HTML_TAGS.TEXTAREA;

  if (!isInput && !isTextArea) return;

  const proto = isTextArea ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;

  let targetVal = typeof preVal === "string" ? preVal : "";

  if (typeof preVal !== "string" && leakedChar && el.value && el.value.endsWith(leakedChar)) {
    targetVal = el.value.slice(0, -leakedChar.length);
  }

  // Reset React internal valueTracker so synthetic change events fire properly
  if (el._valueTracker) {
    el._valueTracker.setValue(targetVal ? targetVal + "___dummy" : "___dummy");
  }

  if (setter) {
    setter.call(el, targetVal);
  } else {
    el.value = targetVal;
  }

  // Dispatch both 'input' and 'change' events with bubbles
  el.dispatchEvent(new Event("input", { bubbles: true }));
  el.dispatchEvent(new Event("change", { bubbles: true }));

  if (preSel && typeof preSel.start === "number" && typeof el.setSelectionRange === "function") {
    try {
      el.setSelectionRange(preSel.start, preSel.end);
    } catch (e) {
      // type="number" or inputs that don't support setSelectionRange
    }
  }
}

export function handleRestrictedInputScannerFocus(bufferVal, scanTargetState) {
  const activeEl = scanTargetState?.el || document.activeElement;
  if (!activeEl) return;

  restoreInputElementValue(activeEl, scanTargetState?.preVal, scanTargetState?.preSel, bufferVal?.[0]);
}

function handleRestrictedScannerBypass(event, scannerBuffer) {
  if (isRestrictedInputActive()) {
    if (scannerBuffer.current.length === 0) {
      return true;
    }
  }
  return false;
}

function handleBatchBarcodePrefix(event, state) {
  const { scannerBuffer, scanTargetRef, hasRestoredRef, onScanStartRef } = state;
  if (event.key !== "$" || scannerBuffer.current.length !== 0) return false;

  event.preventDefault();
  event.stopPropagation();
  event.isHandled = true;
  isScanningBarcodeFlag = true;
  scannerBuffer.current = "$";
  hasRestoredRef.current = true;

  const activeEl = document.activeElement;
  if (
    activeEl &&
    (activeEl.tagName === HTML_TAGS.INPUT || activeEl.tagName === HTML_TAGS.TEXTAREA || activeEl.isContentEditable)
  ) {
    scanTargetRef.current = {
      el: activeEl,
      preVal: activeEl.value ?? "",
      preSel: { start: activeEl.selectionStart, end: activeEl.selectionEnd },
    };
    restoreInputElementValue(activeEl, activeEl.value ?? "");
  }

  if (onScanStartRef?.current) {
    onScanStartRef.current({ firstKey: "$" });
  }
  if (EVENTS?.SCAN_START) {
    eventsEmitter.emit(EVENTS.SCAN_START, { firstKey: "$" });
  }
  return true;
}

function recordScannerKey(event, state) {
  const { scannerBuffer, scanTargetRef, hasRestoredRef } = state;
  if (!event.key || event.key.length !== 1 || event.key === KEYBOARD_KEYS.ENTER) return;

  if (scannerBuffer.current.length === 0) {
    const activeEl = document.activeElement;
    const isTypingField =
      activeEl &&
      (activeEl.tagName === HTML_TAGS.INPUT || activeEl.tagName === HTML_TAGS.TEXTAREA || activeEl.isContentEditable);

    if (isTypingField) {
      scanTargetRef.current = {
        el: activeEl,
        preVal: activeEl.value ?? "",
        preSel: { start: activeEl.selectionStart, end: activeEl.selectionEnd },
      };
    } else {
      scanTargetRef.current = null;
    }
    scannerBuffer.current = event.key;
    hasRestoredRef.current = false;
  } else {
    scannerBuffer.current += event.key;
  }
}

function detectHardwareScannerStream(event, timeDiff, state) {
  const { scannerBuffer, hasRestoredRef, onScanStartRef, scanTargetRef } = state;
  if (scannerBuffer.current.length <= 1 || (timeDiff > CODE_SCANNER_DELAY && !isScanningBarcodeFlag)) {
    return;
  }

  isScanningBarcodeFlag = true;
  event.preventDefault();
  event.stopPropagation();
  event.isHandled = true;

  if (!hasRestoredRef.current) {
    hasRestoredRef.current = true;
    const firstChar = scannerBuffer.current[0];
    if (onScanStartRef?.current) {
      onScanStartRef.current({ firstKey: firstChar });
    }
    if (EVENTS?.SCAN_START) {
      eventsEmitter.emit(EVENTS.SCAN_START, { firstKey: firstChar });
    }
    if (scanTargetRef.current) {
      restoreInputElementValue(
        scanTargetRef.current.el,
        scanTargetRef.current.preVal,
        scanTargetRef.current.preSel,
        firstChar,
      );
    }
  }
}

function handleHardwareScannerEnter(event, state) {
  const { scannerBuffer, scanTimerRef, dispatchScanRef, onScanRef, scanTargetRef, hasRestoredRef } = state;
  if (event.key !== KEYBOARD_KEYS.ENTER || scannerBuffer.current.trim().length < 3) return false;

  event.preventDefault();
  event.stopPropagation();
  event.isHandled = true;
  isScanningBarcodeFlag = true;
  lastScanCompletedTime = Date.now();

  if (scanTimerRef.current) {
    clearTimeout(scanTimerRef.current);
    scanTimerRef.current = null;
  }

  const scannedCode = scannerBuffer.current.trim();
  scannerBuffer.current = "";
  scanTargetRef.current = null;
  hasRestoredRef.current = false;

  if (scannedCode) {
    if (dispatchScanRef?.current) {
      dispatchScanRef.current(scannedCode);
    } else if (onScanRef?.current) {
      onScanRef.current(scannedCode);
    }
  }
  return true;
}

function scheduleHardwareScannerTimer(state) {
  const { scannerBuffer, scanTimerRef, dispatchScanRef, onScanRef, scanTargetRef, hasRestoredRef } = state;
  if (scanTimerRef.current) {
    clearTimeout(scanTimerRef.current);
    scanTimerRef.current = null;
  }

  if (scannerBuffer.current.length < 3) return;

  const currentCode = scannerBuffer.current;
  scanTimerRef.current = setTimeout(() => {
    isScanningBarcodeFlag = false;
    lastScanCompletedTime = Date.now();

    if (scannerBuffer.current === currentCode && scannerBuffer.current.trim().length >= 3) {
      const scannedCode = scannerBuffer.current.trim();
      scannerBuffer.current = "";
      scanTargetRef.current = null;
      hasRestoredRef.current = false;

      if (scannedCode) {
        if (dispatchScanRef?.current) {
          dispatchScanRef.current(scannedCode);
        } else if (onScanRef?.current) {
          onScanRef.current(scannedCode);
        }
      }
    }
  }, CODE_SCANNER_DELAY);
}

function processBarcodeKeyPress(event, state) {
  const { scannerBuffer, scannerLastKeyTime, scanTargetRef, hasRestoredRef } = state;

  if (IGNORE_SCANNER_KEYS?.includes(event.key) || event.isSyntheticUndo) return;
  if (handleRestrictedScannerBypass(event, scannerBuffer)) return;

  const currentTime = Date.now();
  const timeDiff = currentTime - scannerLastKeyTime.current;
  const timeoutThreshold = isScanningBarcodeFlag ? SCAN_STREAM_TIMEOUT : CODE_SCANNER_DELAY;

  if (timeDiff > timeoutThreshold) {
    scannerBuffer.current = "";
    isScanningBarcodeFlag = false;
    scanTargetRef.current = null;
    hasRestoredRef.current = false;
  }

  scannerLastKeyTime.current = currentTime;

  if (handleBatchBarcodePrefix(event, state)) return;

  recordScannerKey(event, state);
  detectHardwareScannerStream(event, timeDiff, state);

  if (handleHardwareScannerEnter(event, state)) return;

  scheduleHardwareScannerTimer(state);
}

function executeQueuedScan(scanQueueRef, onScanRef, scannedCode) {
  const cleanCode = (scannedCode || "").trim();
  if (!cleanCode) return;
  isScanningBarcodeFlag = true;
  scanQueueRef.current = scanQueueRef.current
    .then(async () => {
      if (onScanRef.current) {
        await onScanRef.current(cleanCode);
      }
    })
    .catch((err) => {
      console.error("[useBarcodeScanner] Error in async scan handler:", err);
    })
    .finally(() => {
      isScanningBarcodeFlag = false;
      lastScanCompletedTime = Date.now();
    });
}

/**
 * Custom React hook to handle barcode scanner events via eventsEmitter using SCANNER_TIMEOUT flow.
 *
 * @param {Function} onScan - Callback called with scanned barcode string when completed.
 * @param {Object} [options={}] - Configuration options.
 * @param {boolean} [options.isDisabled=false] - If true, scanner key handling is bypassed.
 * @param {Function} [options.onScanStart] - Callback called when a scan stream is first confirmed.
 */
export function useBarcodeScanner(onScan, options = {}) {
  const { isDisabled = false, onScanStart = null } = options;

  const scannerBuffer = useRef("");
  const scannerLastKeyTime = useRef(0);
  const scanTimerRef = useRef(null);
  const onScanRef = useRef(onScan);
  const onScanStartRef = useRef(onScanStart);
  const scanTargetRef = useRef(null);
  const hasRestoredRef = useRef(false);
  const scanQueueRef = useRef(Promise.resolve());

  const dispatchScan = useCallback((scannedCode) => {
    executeQueuedScan(scanQueueRef, onScanRef, scannedCode);
  }, []);

  const dispatchScanRef = useRef(dispatchScan);
  dispatchScanRef.current = dispatchScan;

  useEffect(() => {
    onScanRef.current = onScan;
    onScanStartRef.current = onScanStart;
  }, [onScan, onScanStart]);

  useEffect(() => {
    if (isDisabled) return;

    const handleKeyDown = (event) => {
      processBarcodeKeyPress(event, {
        scannerBuffer,
        scannerLastKeyTime,
        scanTimerRef,
        onScanRef,
        onScanStartRef,
        dispatchScanRef,
        scanTargetRef,
        hasRestoredRef,
      });
    };

    const unsubscribeFn = eventsEmitter.on(EVENTS.KEY_DOWN, handleKeyDown);
    return () => {
      unsubscribeFn();
      if (scanTimerRef.current) {
        clearTimeout(scanTimerRef.current);
        scanTimerRef.current = null;
      }
      isScanningBarcodeFlag = false;
    };
  }, [isDisabled]);
}

const BarcodeHelper = {
  useBarcodeScanner,
  isScanningBarcode,
  tryParseOnlineOrderScan,
  findMatchingProducts,
  handleMatches,
  parseBatchBarcodeItems,
  processScannedItem,
  processScannedBarcodeItems,
  restoreInputElementValue,
  handleRestrictedInputScannerFocus,
  isRestrictedInputActive,
};

export default BarcodeHelper;
